import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { ExpenseImmobilier, SAMPLE_EXPENSES, ExpenseType, PaymentStatus } from '../models/expense-immobilier';
import { padStart } from '../utils/string.utils';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  })
};

@Injectable({
  providedIn: 'root'
})
export class ExpenseImmobilierService {
  
  private apiUrl = 'https://formation.hybraine.com/mboufta/backend/api/expense-immobilier.php'; // À adapter selon votre backend
  
  // Pour le développement, on utilise des données en mémoire
  private expenses: ExpenseImmobilier[] = [...SAMPLE_EXPENSES];

  constructor(private http: HttpClient) { }

  // ============ GESTION DES DÉPENSES ============

  getExpenses(): Observable<ExpenseImmobilier[]> {
    // En production: return this.http.get<ExpenseImmobilier[]>(`${this.apiUrl}/expenses`);
    return of(this.expenses);
  }

  getExpenseById(id: number): Observable<ExpenseImmobilier> {
    const expense = this.expenses.find(e => e.id === id);
    return of(expense!);
  }

  getExpensesByProperty(propertyId: number): Observable<ExpenseImmobilier[]> {
    const propertyExpenses = this.expenses.filter(e => e.propertyId === propertyId);
    return of(propertyExpenses);
  }

  getExpensesByType(type: ExpenseType): Observable<ExpenseImmobilier[]> {
    const filtered = this.expenses.filter(e => e.type === type);
    return of(filtered);
  }

  getExpensesByDateRange(startDate: Date, endDate: Date): Observable<ExpenseImmobilier[]> {
    const rangeExpenses = this.expenses.filter(e => {
      const expenseDate = new Date(e.expenseDate);
      return expenseDate >= startDate && expenseDate <= endDate;
    });
    return of(rangeExpenses);
  }

  createExpense(expense: ExpenseImmobilier): Observable<ExpenseImmobilier> {
    const reference = `EXP-${new Date().getFullYear()}-${padStart(String(this.expenses.length + 1), 3, '0')}`;
    const newExpense = {
      ...expense,
      id: Math.max(...this.expenses.map(e => e.id), 0) + 1,
      reference,
      totalAmount: expense.amount + (expense.taxAmount || 0),
      createdAt: new Date(),
      updatedAt: new Date()
    };
    this.expenses.push(newExpense);
    // En production: return this.http.post<ExpenseImmobilier>(`${this.apiUrl}/expenses`, expense, httpOptions);
    return of(newExpense);
  }

  updateExpense(id: number, expense: ExpenseImmobilier): Observable<ExpenseImmobilier> {
    const index = this.expenses.findIndex(e => e.id === id);
    if (index !== -1) {
      this.expenses[index] = { 
        ...expense, 
        id, 
        totalAmount: expense.amount + (expense.taxAmount || 0),
        updatedAt: new Date() 
      };
      // En production: return this.http.put<ExpenseImmobilier>(`${this.apiUrl}/expenses/${id}`, expense, httpOptions);
      return of(this.expenses[index]);
    }
    return of(expense);
  }

  deleteExpense(id: number): Observable<boolean> {
    const index = this.expenses.findIndex(e => e.id === id);
    if (index !== -1) {
      this.expenses.splice(index, 1);
      // En production: return this.http.delete<boolean>(`${this.apiUrl}/expenses/${id}`, httpOptions);
      return of(true);
    }
    return of(false);
  }

  searchExpenses(filters: any): Observable<ExpenseImmobilier[]> {
    let filtered = [...this.expenses];
    
    if (filters.type) {
      filtered = filtered.filter(e => e.type === filters.type);
    }
    if (filters.paymentStatus) {
      filtered = filtered.filter(e => e.paymentStatus === filters.paymentStatus);
    }
    if (filters.propertyId) {
      filtered = filtered.filter(e => e.propertyId === filters.propertyId);
    }
    if (filters.startDate && filters.endDate) {
      filtered = filtered.filter(e => {
        const expenseDate = new Date(e.expenseDate);
        return expenseDate >= filters.startDate && expenseDate <= filters.endDate;
      });
    }
    
    return of(filtered);
  }

  // ============ STATISTIQUES ============

  getStatistics(startDate?: Date, endDate?: Date): Observable<any> {
    let filtered = [...this.expenses];
    
    if (startDate && endDate) {
      filtered = filtered.filter(e => {
        const expenseDate = new Date(e.expenseDate);
        return expenseDate >= startDate && expenseDate <= endDate;
      });
    }
    
    const stats = {
      total: filtered.length,
      totalAmount: filtered.reduce((sum, e) => sum + e.totalAmount, 0),
      paidAmount: filtered
        .filter(e => e.paymentStatus === 'paye')
        .reduce((sum, e) => sum + e.totalAmount, 0),
      unpaidAmount: filtered
        .filter(e => e.paymentStatus === 'non_paye' || e.paymentStatus === 'en_retard')
        .reduce((sum, e) => sum + e.totalAmount, 0),
      byType: this.groupBy(filtered, 'type'),
      byProperty: this.groupBy(filtered, 'propertyId')
    };
    return of(stats);
  }

  private groupBy(array: any[], key: string): any {
    return array.reduce((result, item) => {
      const group = item[key] || 'autre';
      if (!result[group]) {
        result[group] = { count: 0, total: 0 };
      }
      result[group].count++;
      result[group].total += item.totalAmount;
      return result;
    }, {});
  }
}
